/**
 * UserMessage — User message with text, file cards, and metadata.
 *
 * Gray pill style, right-aligned. Supports server attachments and local files.
 * Shows max 3 files visible, with a "+N" badge for additional files.
 * Metadata displayed as collapsible JSON cards in a 3-column grid.
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";
import { FileCard } from "./FileCard";
import DOMPurify from "dompurify";
import linkify from "./Utils";

const MAX_VISIBLE_FILES = 3;

const UserMessage = ({
  message,
  messageIndex,
  assistantColors,
  files = [],
  fileMetadataMap = {},
  metadata = null,
  allowMetadata = false,
  onFileMetadataClick,
}) => {
  const { t } = useTranslation();
  const [metadataExpanded, setMetadataExpanded] = useState(false);
  const [showAllFiles, setShowAllFiles] = useState(false);

  const hasFiles = files.length > 0;
  const hasMetadata =
    metadata && typeof metadata === "object" && Object.keys(metadata).length > 0;

  const visibleFiles = showAllFiles ? files : files.slice(0, MAX_VISIBLE_FILES);
  const hasMoreFiles = files.length > MAX_VISIBLE_FILES;
  const remainingCount = files.length - MAX_VISIBLE_FILES;

  const resolveFile = (file) => file.file || file;

  const getFileMetadata = (file) => {
    const fileName =
      file.original_file_name || file.name || (file.file && file.file.name);
    return fileMetadataMap[fileName] || file.metadata || null;
  };

  return (
    <div className="flex justify-end my-6">
      <div className="bg-white rounded-xl max-w-[75%] px-4 py-2.5 text-[15px] shadow-sm sm:max-w-[75%] max-sm:max-w-[90%]">
        {/* Text content */}
        <div
          className="text-[15px] leading-relaxed break-all"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(linkify(message.content.replace(/\n/g, "<br />"))),
          }}
        />

        {/* File attachments */}
        {hasFiles && (
          <div className="flex flex-wrap gap-2 mt-3 items-center">
            {visibleFiles.map((file, i) => (
              <FileCard
                key={i}
                file={resolveFile(file)}
                fileMetadata={getFileMetadata(file)}
                allowMetadata={allowMetadata}
                onMetadataClick={onFileMetadataClick}
                secondaryColor={assistantColors?.secondaryColor}
              />
            ))}
            {hasMoreFiles && !showAllFiles && (
              <button
                type="button"
                onClick={() => setShowAllFiles(true)}
                className="flex items-center justify-center w-11 h-11 rounded-lg border border-gray-200 text-gray-500 hover:bg-white/50 transition-colors cursor-pointer"
                title={t("additionalFiles", { count: remainingCount })}
              >
                <span className="text-xs font-bold">+{remainingCount}</span>
              </button>
            )}
          </div>
        )}

        {/* Message metadata — collapsible JSON cards */}
        {hasMetadata && allowMetadata && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setMetadataExpanded((prev) => !prev)}
              className="flex items-center gap-1 cursor-pointer bg-transparent border-none p-0"
            >
              <h4 className="font-medium m-0 text-sm">{t("Metadata")}</h4>
              {metadataExpanded ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </button>

            {metadataExpanded && (
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(metadata).map(([key, values], idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-md bg-white px-2.5 py-1.5 text-xs font-mono"
                  >
                    <span className="text-gray-500">{key}:</span>{" "}
                    <span>{(Array.isArray(values) ? values : [values]).join(", ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { UserMessage };
